import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Table,
  Pagination,
  Modal,
  Badge,
} from "react-bootstrap";
import BreadCrumb from "Common/BreadCrumb";
import { wishlist } from "api/apis";

interface WishlistItem {
  _id: string;
  count: number;
  productDetails: {
    name: string;
    slug: string;
    _id: string;
  };
  variantDetails: {
    basePrice: number;
    price: number;
    images: string[];
    sku: string;
    quantity: number;
    _id: string;
  };
  productId: string;
  variantId: string;
  userDetails: Array<{
    email: string;
    fullName: string;
    _id: string;
  }>;
  usersCount: number;
}

const Wishlist = () => {
  document.title = "Wishlist";

  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<WishlistItem | null>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const fetchWishlistData = async () => {
      try {
        setLoading(true);
        const response = await wishlist();
        if (response.data.success) {
          setWishlistItems(response.data.wishlistItems);
          setTotalItems(response.data.wishlistItems.length);
        }
      } catch (error) {
        console.error("Error fetching wishlist data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWishlistData();
  }, []);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = wishlistItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleShowDetails = (item: WishlistItem) => {
    setSelectedProduct(item);
    setShowDetailsModal(true);
  };

  // Render pagination items
  const renderPaginationItems = () => {
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Wishlist" pageTitle="Admin" />

          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Customer Wishlist</h5>
                <div>
                  Showing {indexOfFirstItem + 1}-
                  {Math.min(indexOfLastItem, totalItems)} of {totalItems} items
                </div>
              </div>

              <Card>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Table responsive hover className="align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Product</th>
                            {/* <th>Variant</th> */}
                            <th>Price</th>
                            <th>Total Adds</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                No wishlist items found.
                              </td>
                            </tr>
                          ) : (
                            currentItems.map((item, index) => (
                              <tr key={item._id}>
                                <td>{indexOfFirstItem + index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {item.variantDetails.images?.length > 0 && (
                                      <img
                                        src={item.variantDetails.images[0]}
                                        alt={item.productDetails.name}
                                        className="rounded me-2"
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                    <div>
                                      <h6 className="mb-0">
                                        {item.productDetails.name}
                                      </h6>
                                      <small className="text-muted">
                                        SKU: {item.variantDetails.sku}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                {/* <td>
                                  <div>
                                    <div>
                                      Base: ₹{item.variantDetails.basePrice}
                                    </div>
                                    <div>
                                      Selling: ₹{item.variantDetails.price}
                                    </div>
                                  </div>
                                </td> */}
                                <td>₹{item.variantDetails.price}</td>
                                <td>
                                  <Badge bg="primary" pill>
                                    {item.count}{" "}
                                    {item.count === 1 ? "user" : "users"}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleShowDetails(item)}
                                  >
                                    View Details
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>

                      {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <Pagination.Prev
                              disabled={currentPage === 1}
                              onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {renderPaginationItems()}
                            <Pagination.Next
                              disabled={currentPage === totalPages}
                              onClick={() => handlePageChange(currentPage + 1)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Product Details Modal */}
        <Modal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Product Wishlist Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProduct && (
              <div className="row">
                <div className="col-md-4">
                  {selectedProduct.variantDetails.images?.length > 0 && (
                    <img
                      src={selectedProduct.variantDetails.images[0]}
                      alt={selectedProduct.productDetails.name}
                      className="img-fluid rounded mb-3"
                    />
                  )}
                  <h4>{selectedProduct.productDetails.name}</h4>
                  <p className="text-muted">
                    SKU: {selectedProduct.variantDetails.sku}
                  </p>

                  <div className="mb-3">
                    <h6>Pricing</h6>
                    <div>
                      Base Price: ₹{selectedProduct.variantDetails.basePrice}
                    </div>
                    <div>
                      Selling Price: ₹{selectedProduct.variantDetails.price}
                    </div>
                    <div>
                      Quantity: {selectedProduct.variantDetails.quantity}
                    </div>
                  </div>
                </div>

                <div className="col-md-8">
                  <h5 className="mb-3">
                    Added by {selectedProduct.count}{" "}
                    {selectedProduct.count === 1 ? "user" : "users"}
                  </h5>

                  <div className="table-responsive">
                    <Table bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedProduct.userDetails.map((user, index) => (
                          <tr key={user._id}>
                            <td>{index + 1}</td>
                            <td>{user.fullName}</td>
                            <td>{user.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDetailsModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Wishlist;
